<template>
	<ClientTeleport>
		<Transition name="fade">
			<div v-if="visible" ref="popup" class="fixed z-popup inset-0 grid items-end h-full bg-black/20 backdrop-blur-xs" v-bind="$attrs">
				<div class="max-h-screen pt-20 grid items-end h-[100dvh] mx-auto w-full sm:container lg:items-start lg:pt-32 ">
					<div
						ref="popupPanel"
						class="relative flex flex-col bg-background-primary rem:rounded-t-[20px] w-full max-h-full min-h-0 shadow-lg lg:rounded-xl"
						:class="customBodyClasses"
					>
						<div class="flex justify-between max-lg:p-4">
							<slot name="headerLeft" />
							<button
								class="p-2 absolute top-2 right-2 z-20 text-icon-primary hover:bg-button-secondary-surface-hover rounded-full transition ease"
								type="button"
								aria-label="Zavřít"
								@click="close"
							>
								<NuxtIcon name="x" filled class="text-2xl md:text-3xl" />
							</button>
						</div>
						<div
							class="shrink-1 grow-0 max-h-full min-h-0 overflow-y-auto"
							:class="customOverflow ? 'flex flex-col' : 'overflow-auto overscroll-contain'"
						>
							<slot v-bind="slotProps" />
						</div>
					</div>
				</div>
			</div>
		</Transition>
	</ClientTeleport>
</template>

<script lang="ts" setup>
	import {onClickOutside, useScrollLock} from '@vueuse/core';
	import {useFocusTrap} from '@vueuse/integrations/useFocusTrap';
	import {storeToRefs} from 'pinia';
	import {useSelectboxStore} from '~/stores/selectbox';

	type Props = {
		visible: boolean;

		// Set this prop to true if the popup message content handles overflow and scrolling on its own.
		// For example when it makes sense to scroll only inside a table in the content instead of scrolling
		// the entire content.
		customOverflow?: boolean;
		customBodyClasses?: string;
	};

	type Emits = {
		(e: 'update:visible', payload: boolean): void;
	};

	const props = defineProps<Props>();
	const emit = defineEmits<Emits>();

	const body = ref<HTMLElement | null>(null);
	const popup = ref<HTMLElement | null>(null);
	const popupPanel = ref<HTMLElement | null>(null);
	const keydownListener = ref();
	const slotProps = computed(() => ({close}));
	const {selectboxDropdown} = storeToRefs(useSelectboxStore());

	const isLocked = useScrollLock(body);
	const {activate: activateFocusTrap, deactivate: deactivateFocusTrap} = useFocusTrap(popup);

	onClickOutside(popupPanel, close, {ignore: [selectboxDropdown]});

	keydownListener.value = function (e: KeyboardEvent) {
		if (e.key === 'Escape') {
			emit('update:visible', false);
		}
	};

	function close() {
		emit('update:visible', false);
	}

	onMounted(() => {
		document.addEventListener('keydown', keydownListener.value);
		body.value = document.body;
	});

	onBeforeUnmount(() => {
		document.removeEventListener('keydown', keydownListener.value);
		deactivateFocusTrap();
	});

	watch(() => props.visible, async (newValue) => {
		isLocked.value = newValue;
		await nextTick();
		newValue ? activateFocusTrap() : deactivateFocusTrap();
	}, {immediate: true});
</script>
